import type { Optional } from '@festina-life/core'
import { useMemo } from 'react'
import { useLocation, useParams } from 'react-router'

import { useTemplateIsLibrary } from '../../hooks/useTemplateIsLibrary'
import { scratchpad } from '../../system/paths'
import type { AnyConfigurationRouteParams } from '../../system/types'
import { useNavigationParams } from '../../system/useNavigationParams'
import type { ConfigurationItem } from '../../types/common'
import { useAnnulOperationDescriptors } from '../../uiModels/annulOperations/useAnnulOperationDescriptors'
import { useApplicationViewOperationsContext } from '../../workspace/applicationViewOperationsContext'
import { useOperationsContext } from '../../workspace/operationsContext'
import type { TabLink } from './types'

export const useCurrentPathAsTabLink = (): Optional<TabLink> => {
  const {
    templateId,
    parameterDefinitionId,
    enumId,
    registeredTenantId,
    annulOperationKey,
    mappingId,
    specId,
  } = useParams<AnyConfigurationRouteParams>()
  const { layer, server, branch, block } = useNavigationParams()
  const location = useLocation()
  const { workspaceKey, allAnnulOperationsByKey } = useOperationsContext()
  const {
    applicationTemplates,
    applicationParameterDefinitions,
    applicationParameterValues,
    applicationRegisteredTenants,
    applicationEnumDefinitions,
    applicationGlMaps,
    applicationReconciliationSpecs,
  } = useApplicationViewOperationsContext()
  const templateIsLibrary = useTemplateIsLibrary()
  const descriptors = useAnnulOperationDescriptors()
  const scratchPadLocation = scratchpad({
    server,
    branch,
    layer,
    block,
    workspaceName: workspaceKey?.name,
  })
  const isCurrentLocationScratchPad = scratchPadLocation === location.pathname

  return useMemo(() => {
    const currentLayerKey = { layerId: layer.layerId, tenantId: layer.tenantId }
    if (isCurrentLocationScratchPad) {
      return {
        variant: 'scratchpad',
        isActive: true,
        id: 'scratchpad',
        isDraft: false,
        layer: currentLayerKey,
        block,
      }
    }

    if (annulOperationKey) {
      const annulOp = allAnnulOperationsByKey.get(annulOperationKey)
      const descriptor = annulOp?.internalOperation.createOperationClassName
        ? descriptors[annulOp?.internalOperation.createOperationClassName]
        : undefined

      return {
        id: annulOperationKey,
        label: annulOp?.internalOperation.name ?? undefined,
        variant: 'annulOperation',
        isActive: true,
        isDraft: !!annulOp?.isDraft,
        annulledEntityVariant: descriptor?.configurationItemVariant,
        layer: currentLayerKey,
        block,
      }
    }

    const currentRouteId =
      templateId ??
      parameterDefinitionId ??
      enumId ??
      registeredTenantId ??
      mappingId ??
      specId

    if (currentRouteId) {
      let linkVariant: ConfigurationItem
      let isDraft: boolean

      if (templateId) {
        linkVariant = templateIsLibrary(templateId) ? 'library' : 'template'
        isDraft = !!applicationTemplates.get(templateId)?.isDraft
      } else if (parameterDefinitionId) {
        linkVariant = 'parameterDefinition'

        isDraft =
          !!applicationParameterValues
            ?.get(parameterDefinitionId)
            ?.some((operation) => operation.isDraft) ||
          !!applicationParameterDefinitions?.get(parameterDefinitionId)?.isDraft
      } else if (registeredTenantId) {
        linkVariant = 'tenant'
        isDraft = !!applicationRegisteredTenants?.some(
          (operation) => operation.isDraft,
        )
      } else if (enumId) {
        linkVariant = 'enumDefinition'
        isDraft =
          !!applicationEnumDefinitions.get(enumId)?.isDraft ||
          !!applicationEnumDefinitions
            ?.get(enumId)
            ?.literals.some(
              (literal) => literal.type === 'configured' && literal.isDraft,
            )
      } else if (mappingId) {
        linkVariant = 'glMap'
        isDraft = !!applicationGlMaps.get(mappingId)?.isDraft
      } else if (specId) {
        linkVariant = 'reconciliationSpec'
        isDraft = !!applicationReconciliationSpecs.get(specId)?.isDraft
      } else {
        throw new Error(`Unsupported link type for ${currentRouteId}`)
      }

      return {
        id: currentRouteId,
        variant: linkVariant,
        isActive: true,
        isDraft,
        layer: currentLayerKey,
        block,
      }
    }

    return null
  }, [
    templateIsLibrary,
    templateId,
    applicationTemplates,
    layer,
    registeredTenantId,
    applicationParameterValues,
    enumId,
    isCurrentLocationScratchPad,
    applicationRegisteredTenants,
    parameterDefinitionId,
    applicationEnumDefinitions,
    applicationParameterDefinitions,
    block,
    annulOperationKey,
    allAnnulOperationsByKey,
    descriptors,
    mappingId,
    applicationGlMaps,
    specId,
    applicationReconciliationSpecs,
  ])
}
